import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FileSpreadsheet,
  IndianRupee,
  Loader2,
  Search,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";
import PayrollRunSelect from "../../components/common/PayrollRunSelect";
import { resolveEmployeeDisplay } from "../../components/common/displayUtils";
import { useEmployees } from "../../hooks/useEmployees";
import { getSalaryStatement } from "./payrollApi";
import { usePrefilledPayrollRunId } from "./payrollWorkflow";
import {
  HrHero,
  HrPanel,
  HrBtn,
  HrInput,
  HrEmptyState,
} from "../../components/hr/HrDesignSystem";
import "../../styles/IndianCalendar.css";

function formatCurrency(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function getInitials(name) {
  if (!name || name === "—") return "?";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function avatarHue(name) {
  let hash = 0;
  const s = String(name || "");
  for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
  const hues = ["#4f46e5", "#0f766e", "#0369a1", "#7c3aed", "#c2410c", "#15803d"];
  return hues[Math.abs(hash) % hues.length];
}

function componentTone(code = "", name = "") {
  const key = `${code} ${name}`.toUpperCase();
  if (/PF|EPF|ESI|TDS|DEDUCT|LOAN|PT\b/.test(key)) return "deduction";
  if (/BASIC|HRA|ALLOW|BONUS|SPECIAL|EARN/.test(key)) return "earning";
  return "neutral";
}

function matchesStatutoryFocus(focus, code = "", name = "") {
  const key = `${code} ${name}`.toUpperCase();
  if (focus === "pf") return /\b(PF|EPF|EPS|EDLI|PF_ADMIN)\b/.test(key) || /PROVIDENT/.test(key);
  if (focus === "esi") return /\bESI\b/.test(key) || /EMPLOYEE STATE INSURANCE/.test(key);
  return true;
}

function filterStatementByFocus(emp, focus) {
  if (!focus || focus === "all") return emp;
  const lines = (emp.component_lines || []).filter((ln) =>
    matchesStatutoryFocus(focus, ln.component_code, ln.name)
  );
  const components = Object.fromEntries(
    Object.entries(emp.components || {}).filter(([name]) =>
      matchesStatutoryFocus(focus, "", name)
    )
  );
  return { ...emp, component_lines: lines, components };
}

const FOCUS_META = {
  pf: {
    title: "PF report",
    subtitle: "Employee and employer provident fund contributions from processed payroll runs.",
    statLabel: "PF lines",
  },
  esi: {
    title: "ESI report",
    subtitle: "Employee State Insurance contributions from processed payroll runs.",
    statLabel: "ESI lines",
  },
};

function EmployeeStatementCard({ emp, employees, index }) {
  const displayName = resolveEmployeeDisplay(emp, employees);
  const hue = avatarHue(displayName);
  const lines = emp.component_lines || [];
  const earnings = lines.filter((ln) => componentTone(ln.component_code, ln.name) === "earning");
  const deductions = lines.filter((ln) => componentTone(ln.component_code, ln.name) === "deduction");
  const other = lines.filter((ln) => componentTone(ln.component_code, ln.name) === "neutral");

  const totalFinal = lines.reduce((sum, ln) => sum + (Number(ln.amount) || 0), 0);
  const totalOriginal = lines.reduce((sum, ln) => sum + (Number(ln.original_amount) || 0), 0);
  const hasProration = lines.some((ln) => ln.proration_applied);

  return (
    <motion.article
      className="sal-stmt-card"
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: Math.min(index * 0.04, 0.4) }}
    >
      <div className="sal-stmt-card-head">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="sal-avatar shrink-0"
            style={{
              background: `linear-gradient(135deg, ${hue}22, ${hue}44)`,
              color: hue,
            }}
          >
            {getInitials(displayName)}
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-bold text-slate-900 truncate">{displayName}</h3>
            {emp.employee_code ? (
              <p className="text-xs text-slate-500 font-mono mt-0.5">{emp.employee_code}</p>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {hasProration ? (
            <span className="sal-stmt-badge sal-stmt-badge--warn">
              <TrendingUp size={12} />
              Prorated
            </span>
          ) : null}
          <span className="sal-stmt-badge sal-stmt-badge--total">
            <IndianRupee size={12} />
            {formatCurrency(totalFinal)}
          </span>
        </div>
      </div>

      {lines.length > 0 ? (
        <div className="sal-stmt-table-wrap">
          <table className="sal-stmt-table">
            <thead>
              <tr>
                <th className="sal-stmt-th">Component</th>
                <th className="sal-stmt-th text-right">Original</th>
                <th className="sal-stmt-th text-right">Final</th>
                <th className="sal-stmt-th text-center">Prorated</th>
              </tr>
            </thead>
            <tbody>
              {[...earnings, ...other, ...deductions].map((ln) => {
                const tone = componentTone(ln.component_code, ln.name);
                const changed =
                  Number(ln.original_amount) !== Number(ln.amount) && ln.original_amount != null;
                return (
                  <tr key={`${ln.component_code}-${ln.name}`} className="sal-stmt-row group">
                    <td className="sal-stmt-td">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`sal-stmt-dot sal-stmt-dot--${tone}`} />
                        <div className="min-w-0">
                          <p className="font-medium text-slate-800 truncate">{ln.name}</p>
                          {ln.component_code ? (
                            <p className="text-[10px] text-slate-400 font-mono">{ln.component_code}</p>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td className="sal-stmt-td text-right tabular-nums text-slate-600">
                      {formatCurrency(ln.original_amount)}
                    </td>
                    <td className="sal-stmt-td text-right">
                      <span
                        className={`font-semibold tabular-nums ${
                          changed ? "text-teal-700" : "text-slate-900"
                        }`}
                      >
                        {formatCurrency(ln.amount)}
                      </span>
                    </td>
                    <td className="sal-stmt-td text-center">
                      {ln.proration_applied ? (
                        <span className="sal-stmt-pill sal-stmt-pill--yes">Yes</span>
                      ) : ln.attendance_proratable === false ? (
                        <span className="sal-stmt-pill sal-stmt-pill--fixed">Fixed</span>
                      ) : (
                        <span className="sal-stmt-pill sal-stmt-pill--no">No</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="sal-stmt-foot">
                <td className="sal-stmt-td font-semibold text-slate-700">Total</td>
                <td className="sal-stmt-td text-right tabular-nums font-medium text-slate-600">
                  {formatCurrency(totalOriginal)}
                </td>
                <td className="sal-stmt-td text-right tabular-nums font-bold text-teal-800">
                  {formatCurrency(totalFinal)}
                </td>
                <td className="sal-stmt-td" />
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4">
          {Object.entries(emp.components || {}).map(([k, v]) => (
            <div key={k} className="sal-stmt-mini-stat">
              <p className="text-[10px] uppercase tracking-wide text-slate-500">{k}</p>
              <p className="text-sm font-bold text-slate-900 mt-1">{formatCurrency(v)}</p>
            </div>
          ))}
        </div>
      )}
    </motion.article>
  );
}

export default function SalaryStatementPage() {
  const [searchParams] = useSearchParams();
  const focus = (searchParams.get("focus") || "").toLowerCase();
  const focusMeta = FOCUS_META[focus] || null;
  const { employees } = useEmployees();
  const [id, setId] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [loaded, setLoaded] = useState(false);

  usePrefilledPayrollRunId(setId);

  const load = useCallback(async () => {
    if (!id) {
      setError("Please select a payroll run.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await getSalaryStatement(id);
      setData(Array.isArray(res.data?.salary_statement) ? res.data.salary_statement : []);
      setLoaded(true);
    } catch (e) {
      setData([]);
      setError(e?.response?.data?.detail || e.message || "Failed to load salary statement");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const scoped = focusMeta
      ? data.map((emp) => filterStatementByFocus(emp, focus))
      : data;
    if (!q) return scoped;
    return scoped.filter((emp) => {
      const label = resolveEmployeeDisplay(emp, employees).toLowerCase();
      return label.includes(q) || String(emp.employee_code || "").toLowerCase().includes(q);
    });
  }, [data, search, employees, focus, focusMeta]);

  const stats = useMemo(() => {
    const employeeCount = filtered.length;
    let componentLines = 0;
    let proratedLines = 0;
    let grandTotal = 0;
    for (const emp of filtered) {
      const lines = emp.component_lines || [];
      componentLines += lines.length;
      proratedLines += lines.filter((ln) => ln.proration_applied).length;
      grandTotal += lines.reduce((sum, ln) => sum + (Number(ln.amount) || 0), 0);
    }
    return { employeeCount, componentLines, proratedLines, grandTotal };
  }, [filtered]);

  return (
    <div className="w-full max-w-full min-w-0 p-4 md:p-6 space-y-5">
      <HrHero
        eyebrow={
          <span className="inline-flex items-center gap-2">
            <FileSpreadsheet size={15} />
            Payroll reports
          </span>
        }
        title={focusMeta?.title || "Salary statement"}
        subtitle={
          focusMeta?.subtitle ||
          "Component-wise salary breakdown per employee for any payroll run — with original vs final amounts and attendance proration detail."
        }
        stats={[
          { label: "Employees", value: loaded ? stats.employeeCount : "—" },
          {
            label: focusMeta?.statLabel || "Components",
            value: loaded ? stats.componentLines : "—",
          },
          { label: "Prorated lines", value: loaded ? stats.proratedLines : "—" },
          {
            label: "Combined total",
            value: loaded && stats.grandTotal ? formatCurrency(stats.grandTotal) : "—",
          },
        ]}
      />

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.05 }}
      >
        <HrPanel title="Select payroll run" subtitle="Choose a processed run, then load live payroll data.">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[16rem]">
              <PayrollRunSelect
                value={id}
                onChange={(runId) => {
                  setId(runId);
                  setLoaded(false);
                }}
                label="Payroll run"
                selectClassName="hr-input w-full"
                hint=""
              />
            </div>
            <HrBtn variant="primary" onClick={load} disabled={loading || !id}>
              {loading ? <Loader2 size={15} className="animate-spin" /> : <Wallet size={15} />}
              {loading ? "Loading…" : "Load statement"}
            </HrBtn>
          </div>
          {error ? (
            <p className="mt-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              {error}
            </p>
          ) : null}
        </HrPanel>
      </motion.div>

      {loaded && data.length > 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1 }}
        >
          <HrPanel
            title="Employee breakdown"
            subtitle={`${filtered.length} of ${data.length} employee${data.length === 1 ? "" : "s"}`}
            headerRight={
              <div className="relative w-full sm:w-56">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
                <HrInput
                  className="!pl-9 !py-2"
                  placeholder="Search by name or code…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            }
            noPadding
          >
            <div className="p-4 md:p-5 space-y-4 sal-stmt-list">
              {filtered.length === 0 ? (
                <HrEmptyState
                  icon={Users}
                  title="No employees match your search"
                  description="Try a different name or employee code."
                />
              ) : (
                filtered.map((emp, idx) => (
                  <EmployeeStatementCard
                    key={emp.employee_id}
                    emp={emp}
                    employees={employees}
                    index={idx}
                  />
                ))
              )}
            </div>
          </HrPanel>
        </motion.div>
      ) : loaded && data.length === 0 ? (
        <HrEmptyState
          icon={FileSpreadsheet}
          title="No salary data for this run"
          description="Process payroll for this run first, or choose a different payroll run."
        />
      ) : loaded && focusMeta && filtered.every((emp) => !(emp.component_lines || []).length) ? (
        <HrEmptyState
          icon={FileSpreadsheet}
          title={`No ${focus.toUpperCase()} components in this run`}
          description="Enable the statutory component in Settings, reprocess payroll, then reload this report."
        />
      ) : !loaded ? (
        <div className="sal-stmt-placeholder">
          <FileSpreadsheet className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="font-semibold text-slate-600">No statement loaded yet</p>
          <p className="text-sm text-slate-500 mt-1">Select a payroll run and click Load statement.</p>
        </div>
      ) : null}
    </div>
  );
}
